import { createSession } from '@/app/(intakeFormV2)/intake-v2/contexts/intakeSessionApi';
import {
  getStoredSessionId,
  storeSessionId,
} from '@/app/(intakeFormV2)/intake-v2/contexts/intakeSessionIdStorage';
import { IntakeSessionSource } from '@/app/(intakeFormV2)/intake-v2/types';
import { SalesforceCalculatePriceResponse } from '@/types/salesforce';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';

const URL = `${process.env.NEXT_PUBLIC_MARKETING_API_URL}/v1/state`;

export type StateInformation = {
  stepId: number | string | undefined;
  stepIndex: number | string | undefined;
  stepName: string | undefined;
};

type TrackStateChangePayloadQuote = { quote: SalesforceCalculatePriceResponse };

export type TrackStateChangePayload = {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email_address?: string;
  postal_code?: string;
  marketing_consent?: boolean;
  visitedLocations?: string[];
  referrer?: string;
} & Partial<TrackStateChangePayloadQuote> & { leadId: string | undefined };

export type StateChangeParams = {
  source: IntakeSessionSource;
  stateInformation: StateInformation;
  payload: undefined | TrackStateChangePayload;
};

type TrackStateChangeRequest = StateChangeParams;

type TrackStateChangeResponse = StateChangeParams & { id: string };

const ensureSessionId = async (
  source: IntakeSessionSource
): Promise<string> => {
  const existingSessionId = getStoredSessionId();

  if (existingSessionId) return existingSessionId;

  const { sessionId } = await createSession(source);

  storeSessionId(sessionId);

  return sessionId;
};
const trackStateChange = async ({
  source,
  stateInformation,
  payload,
}: StateChangeParams) => {
  const sessionId = await ensureSessionId(source);

  try {
    const response = await fetch(`${URL}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        source,
        payload: { ...payload, last_session_date: Date.now() },
        stateInformation,
      }),
    });

    return response.json();
  } catch (error) {
    console.error('Failed to note state change:', error);

    throw error;
  }
};

const TRACK_STATE_CHANGE_KEY = ['state', 'track-change'] as const;

export const useStateChangeTrackingMutation = (
  options?: Omit<
    UseMutationOptions<
      TrackStateChangeResponse,
      Error,
      TrackStateChangeRequest
    >,
    'mutationFn'
  >
) =>
  useMutation<TrackStateChangeResponse, Error, TrackStateChangeRequest>({
    mutationKey: TRACK_STATE_CHANGE_KEY,
    mutationFn: trackStateChange,
    ...options,
  });
